import type { Schemas } from '@npc/api-client'
import { Link } from '@tanstack/react-router'
import { useMemo, useState, type ReactNode } from 'react'

import { ago } from '../../app/format'
import { parentName, statusTone } from '../../app/host-status'
import { Button } from '../../components/button'
import { buttonClass } from '../../components/button-style'
import { Card } from '../../components/card'
import { cn } from '../../components/cn'
import { Page, PageHeader, SectionTitle } from '../../components/page'
import { StatusDot, Tag } from '../../components/tag'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { ReparentRow } from './reparent'
import './hosts.css'

/**
 * 全局机器池。
 *
 * 这一页不是库存表，也不是把五列表套一层卡片皮。
 * 顶上总览数一眼；琥珀条只放要你点头的；下面三组互不重叠。
 */

export type AcceptSuggestionWhat =
  | 'identity'
  | 'parent'
  | 'reject_standalone'
  | 'reject_keep'

export interface HostsViewProps {
  hosts: Schemas['HostSummary'][]
  unassignedHosts: Schemas['HostSummary'][]
  totalCount?: number
  projects: Schemas['Project'][]
  onAcceptSuggestion: (hostId: string, what: AcceptSuggestionWhat) => void
  onAssignProject: (hostId: string, projectId: string) => void
  acceptingHostId?: string
  assigningHostId?: string
}

type Bucket = 'decision' | 'available' | 'assigned' | 'exception'

interface Row {
  host: Schemas['HostSummary']
  children: Schemas['HostSummary'][]
}

export function HostsView({
  hosts,
  unassignedHosts,
  totalCount,
  projects,
  onAcceptSuggestion,
  onAssignProject,
  acceptingHostId,
  assigningHostId,
}: HostsViewProps) {
  const projectById = useMemo(
    () => new Map(projects.map((project) => [project.id, project])),
    [projects],
  )
  const byId = useMemo(() => {
    const map = new Map<string, Schemas['HostSummary']>()
    for (const host of hosts) map.set(host.id, host)
    for (const host of unassignedHosts) {
      if (!map.has(host.id)) map.set(host.id, host)
    }
    return map
  }, [hosts, unassignedHosts])
  const all = useMemo(() => [...byId.values()], [byId])

  const decision = useMemo(() => {
    const ids = new Set<string>()
    for (const host of unassignedHosts) ids.add(host.id)
    for (const host of all) {
      if (needsNod(host)) ids.add(host.id)
    }
    return [...ids].map((id) => byId.get(id)!).filter(Boolean)
  }, [all, byId, unassignedHosts])

  const decisionIds = useMemo(() => new Set(decision.map((host) => host.id)), [decision])

  const buckets = useMemo(() => splitPool(all, decisionIds), [all, decisionIds])

  const remaining = Math.max(0, (totalCount ?? hosts.length) - hosts.length)
  const empty = hosts.length === 0 && unassignedHosts.length === 0

  return (
    <Page className="hosts-theme min-h-full bg-m-bg0 text-npc-ink">
      <PageHeader
        title={<T id={empty ? 'host.list.empty.title' : 'host.list.title'} />}
        actions={
          <Link to="/hosts/new" className={buttonClass({ variant: 'primary' })}>
            <T id="dashboard.action.add_host" />
          </Link>
        }
      />
      {empty ? null : (
        <p className="mb-npc-modal mt-npc-gap-xs max-w-[720px] text-npc-body-lg text-npc-ink-soft">
          <T id="host.list.intro_ready" />
        </p>
      )}

      {empty ? null : (
        <Overview
          total={totalCount ?? all.length}
          pending={decision.length}
          available={countHosts(buckets.available)}
          assigned={countHosts(buckets.assigned)}
          exception={countHosts(buckets.exception)}
        />
      )}

      {decision.length > 0 ? (
        <DecisionBar
          hosts={decision}
          projectById={projectById}
          onAccept={onAcceptSuggestion}
          onAssign={onAssignProject}
          acceptingHostId={acceptingHostId}
          assigningHostId={assigningHostId}
        />
      ) : null}

      {empty ? (
        <Card className="max-w-[560px]" data-testid="hosts-empty">
          <SectionTitle className="mb-[7px]">{t('host.list.empty.title')}</SectionTitle>
          <p className="m-0 text-npc-body-sm leading-[1.7] text-npc-ink-soft">
            {t('host.list.empty.body')}
          </p>
          <div className="mt-npc-grid-sm">
            <Link to="/hosts/new" className={buttonClass({ variant: 'primary' })}>
              <T id="dashboard.action.add_host" />
            </Link>
          </div>
        </Card>
      ) : buckets.available.length + buckets.assigned.length + buckets.exception.length > 0 ? (
        <div data-testid="hosts-pool" className="flex flex-col gap-npc-grid-lg">
          <HostGroup
            testId="hosts-group-available"
            titleId="host.group.available"
            rows={buckets.available}
            projectById={projectById}
          />
          <HostGroup
            testId="hosts-group-assigned"
            titleId="host.group.assigned"
            rows={buckets.assigned}
            projectById={projectById}
          />
          <HostGroup
            testId="hosts-group-exception"
            titleId="host.group.exception"
            rows={buckets.exception}
            projectById={projectById}
          />
          {remaining > 0 ? (
            <div className="px-npc-gap-xs py-npc-grid-sm text-npc-caption text-npc-ink-softer">
              <T id="host.list.more" vars={{ count: remaining }} />
            </div>
          ) : null}
        </div>
      ) : remaining > 0 ? (
        <div className="px-npc-gap-xs py-npc-grid-sm text-npc-caption text-npc-ink-softer">
          <T id="host.list.more" vars={{ count: remaining }} />
        </div>
      ) : null}

      {empty ? (
        <div className="mt-npc-grid-lg grid grid-cols-2 gap-npc-grid max-[600px]:grid-cols-1">
          <Card>
            <SectionTitle className="mb-[7px]">
              <T id="host.list.project_first.title" />
            </SectionTitle>
            <p className="m-0 mb-npc-grid-sm text-npc-body-sm leading-[1.65] text-npc-ink-soft">
              <T id="host.list.project_first.body" />
            </p>
            <Link to="/projects/new" className={buttonClass({ size: 'md' })}>
              <T id="host.list.project_first.cta" />
            </Link>
          </Card>
          <Card>
            <SectionTitle className="mb-[7px]">
              <T id="host.list.mixed.title" />
            </SectionTitle>
            <p className="m-0 text-npc-body-sm leading-[1.65] text-npc-ink-soft">
              <T id="host.list.mixed.body" />
              <span className="text-npc-amber">
                <T id="host.list.mixed.body_warn" />
              </span>
            </p>
          </Card>
        </div>
      ) : null}
    </Page>
  )
}

function Overview({
  total,
  pending,
  available,
  assigned,
  exception,
}: {
  total: number
  pending: number
  available: number
  assigned: number
  exception: number
}) {
  const items = [
    { id: 'host.overview.all' as const, value: total, warn: false },
    { id: 'host.overview.pending' as const, value: pending, warn: pending > 0 },
    { id: 'host.overview.available' as const, value: available, warn: false },
    { id: 'host.overview.assigned' as const, value: assigned, warn: false },
    { id: 'host.overview.exception' as const, value: exception, warn: exception > 0 },
  ]
  return (
    <div
      data-testid="hosts-overview"
      className="mb-npc-card-lg grid grid-cols-5 gap-npc-grid max-[600px]:grid-cols-1"
    >
      {items.map((item) => {
        const pendingChip = item.id === 'host.overview.pending' && item.warn
        return (
        <Card
          key={item.id}
          tone={pendingChip ? 'plain' : item.warn ? 'amber' : 'plain'}
          className={pendingChip ? 'hosts-overview-pending' : undefined}
          data-testid={pendingChip ? 'hosts-overview-pending' : undefined}
        >
          <div
            className={cn(
              'mb-npc-gap-sm text-npc-caption',
              pendingChip
                ? 'hosts-overview-pending-label'
                : item.warn
                  ? 'text-npc-amber-ink'
                  : 'text-npc-ink-soft',
            )}
          >
            <T id={item.id} />
          </div>
          <div
            className={cn(
              'font-npc-display text-npc-stat font-semibold',
              pendingChip && 'hosts-overview-pending-value',
            )}
          >
            {item.value}
          </div>
        </Card>
        )
      })}
    </div>
  )
}

function DecisionBar({
  hosts,
  projectById,
  onAccept,
  onAssign,
  acceptingHostId,
  assigningHostId,
}: {
  hosts: Schemas['HostSummary'][]
  projectById: Map<string, Schemas['Project']>
  onAccept: (hostId: string, what: AcceptSuggestionWhat) => void
  onAssign: (hostId: string, projectId: string) => void
  acceptingHostId?: string
  assigningHostId?: string
}) {
  const hasProjectGuess = hosts.some((host) => Boolean(host.suggestion?.project_id))
  return (
    <Card tone="amber" className="mb-npc-grid-lg" data-testid="hosts-decision">
      <div className="mb-[4px] flex items-center gap-npc-gap-sm">
        <StatusDot tone="warn" />
        <div className="text-npc-body-lg font-semibold text-npc-amber-ink">
          <T id="host.list.decision_title" vars={{ count: hosts.length }} />
        </div>
      </div>
      <div className="mb-npc-gap-lg pl-npc-card-sm text-npc-body-sm text-npc-amber-ink">
        {hasProjectGuess ? <T id="host.list.unassigned_hint" /> : <T id="host.list.decision_hint" />}
      </div>
      <div className="flex flex-col gap-npc-gap-sm">
        {hosts.map((host) => (
          <HostCard
            key={host.id}
            row={{ host, children: [] }}
            projectById={projectById}
            decision
            onAccept={onAccept}
            onAssign={onAssign}
            acceptingHostId={acceptingHostId}
            assigningHostId={assigningHostId}
          />
        ))}
      </div>
    </Card>
  )
}

function HostGroup({
  testId,
  titleId,
  rows,
  projectById,
}: {
  testId: string
  titleId: 'host.group.available' | 'host.group.assigned' | 'host.group.exception'
  rows: Row[]
  projectById: Map<string, Schemas['Project']>
}) {
  if (rows.length === 0) return null
  return (
    <section data-testid={testId}>
      <SectionTitle className="mb-npc-gap-sm text-npc-ink-soft">
        <T id={titleId} />
      </SectionTitle>
      <div className="flex flex-col gap-npc-gap-sm">
        {rows.map((row) => (
          <HostCard key={row.host.id} row={row} projectById={projectById} />
        ))}
      </div>
    </section>
  )
}

function HostCard({
  row,
  projectById,
  decision = false,
  onAccept,
  onAssign,
  acceptingHostId,
  assigningHostId,
}: {
  row: Row
  projectById: Map<string, Schemas['Project']>
  decision?: boolean
  onAccept?: (hostId: string, what: AcceptSuggestionWhat) => void
  onAssign?: (hostId: string, projectId: string) => void
  acceptingHostId?: string
  assigningHostId?: string
}) {
  const [open, setOpen] = useState(true)
  const hasChildren = row.children.length > 0
  const splitCount = row.host.split_count ?? (hasChildren ? row.children.length : undefined)

  return (
    <div
      data-pool-card=""
      data-unassigned-card={decision || undefined}
      className={cn(
        'rounded-npc border p-npc-card',
        decision
          ? 'border-npc-amber-border bg-npc-panel'
          : 'border-npc-border bg-npc-panel hover:bg-npc-surface-warm',
      )}
    >
      <HostFacts host={row.host} projectById={projectById} splitCount={splitCount}>
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setOpen((value) => !value)
            }}
            className="relative z-10 min-h-[44px] min-w-[44px] cursor-pointer border-none bg-transparent p-0 text-npc-meta text-npc-ink-softer"
          >
            {open ? '▾' : '▶'}
          </button>
        ) : null}
      </HostFacts>
      <HostCardBody
        host={row.host}
        projectById={projectById}
        decision={decision}
        onAccept={onAccept}
        onAssign={onAssign}
        acceptingHostId={acceptingHostId}
        assigningHostId={assigningHostId}
      />
      {hasChildren && open
        ? row.children.map((child) => (
            <div key={child.id} data-indented className="mt-npc-gap-sm pl-npc-grid-lg">
              <HostChildCard host={child} />
            </div>
          ))
        : null}
    </div>
  )
}

function HostFacts({
  host,
  projectById,
  splitCount,
  children,
}: {
  host: Schemas['HostSummary']
  projectById: Map<string, Schemas['Project']>
  splitCount?: number
  children?: ReactNode
}) {
  const projects = host.projects
  return (
    <div className="relative">
      <Link
        to="/hosts/$hostId"
        params={{ hostId: host.id }}
        aria-label={host.name}
        className="absolute inset-0"
      />
      <div className="relative flex items-start gap-npc-gap-xs">
        {children}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-npc-gap-xs">
            <div className="min-w-0 text-npc-body font-medium">{host.name}</div>
            {projects.length === 0 ? (
              <Tag tone="neutral">
                <T id="host.occupancy.unassigned" />
              </Tag>
            ) : (
              projects.map((project) => (
                <Tag
                  key={project.id}
                  tone={projectById.get(project.id)?.is_builtin ? 'info' : 'neutral'}
                >
                  {project.name}
                </Tag>
              ))
            )}
            <StatusChip host={host} />
          </div>
          <HostMetaStrip host={host} />
          <HostAddresses host={host} />
          <HostWorkloads host={host} />
          <p className="mb-0 mt-npc-gap-xs line-clamp-2 text-npc-caption leading-[1.5] text-npc-ink-medium">
            {hostSummaryText(host.summary)}
          </p>
          {splitCount ? (
            <div className="mt-[2px] text-npc-meta-sm text-npc-ink-softer">
              <T id="host.carve.split_count" vars={{ count: splitCount }} />
            </div>
          ) : null}
          {parentName(host.parent) ? (
            <div className="mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
              <T id="host.carve.lives_on" vars={{ parent_name: parentName(host.parent)! }} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function HostCardBody({
  host,
  projectById,
  decision = false,
  onAccept,
  onAssign,
  acceptingHostId,
  assigningHostId,
}: {
  host: Schemas['HostSummary']
  projectById: Map<string, Schemas['Project']>
  decision?: boolean
  onAccept?: (hostId: string, what: AcceptSuggestionWhat) => void
  onAssign?: (hostId: string, projectId: string) => void
  acceptingHostId?: string
  assigningHostId?: string
}) {
  const [picking, setPicking] = useState(false)
  const guessed = host.suggestion?.project_id
    ? projectById.get(host.suggestion.project_id)
    : undefined
  const busy = acceptingHostId === host.id || assigningHostId === host.id
  const projectChoices = [...projectById.values()]
  const credential = host.reach?.kind === 'credential'

  return (
    <div className="relative mt-npc-gap-sm">
      <div
        className={cn(
          'flex flex-col gap-npc-gap-xs sm:flex-row sm:flex-wrap',
          decision ? undefined : 'sm:justify-end',
        )}
      >
        {decision && guessed && onAccept ? (
          <Button
            variant="primary"
            disabled={busy}
            className="relative z-10"
            onClick={(event) => {
              event.stopPropagation()
              onAccept(host.id, 'identity')
            }}
          >
            <T id="host.list.suggestion_accept" vars={{ project_name: guessed.name }} />
          </Button>
        ) : null}
        {decision && onAssign ? (
          <Button
            disabled={busy || projectChoices.length === 0}
            className="relative z-10"
            onClick={(event) => {
              event.stopPropagation()
              setPicking((value) => !value)
            }}
          >
            <T id="host.list.suggestion_pick" />
          </Button>
        ) : null}
        {credential ? (
          <Link
            to="/hosts/new"
            className={cn(buttonClass({ size: 'md' }), 'relative z-10')}
            onClick={(event) => event.stopPropagation()}
          >
            <T id="host.action.reconnect" />
          </Link>
        ) : null}
        <Link
          to="/hosts/$hostId"
          params={{ hostId: host.id }}
          className={cn(buttonClass({ size: 'md' }), 'relative z-10')}
          onClick={(event) => event.stopPropagation()}
        >
          <T id="host.card.detail" />
        </Link>
      </div>
      {decision && onAccept ? (
        <ReparentRow
          host={host}
          onAccept={onAccept}
          onReject={(hostId, kind) =>
            onAccept(hostId, kind === 'standalone' ? 'reject_standalone' : 'reject_keep')
          }
          accepting={busy}
        />
      ) : null}
      {picking && onAssign ? (
        <div className="mt-npc-gap-sm flex flex-wrap gap-[7px] border-t border-npc-border-softer pt-npc-gap-sm">
          {projectChoices.map((project) => (
            <Button
              key={project.id}
              disabled={busy}
              className="relative z-10"
              onClick={(event) => {
                event.stopPropagation()
                onAssign(host.id, project.id)
              }}
            >
              {project.name}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function HostChildCard({ host }: { host: Schemas['HostSummary'] }) {
  const { primary } = pickPrimaryAddress(hostAddresses(host))
  const showIp = Boolean(primary && primary !== host.name)
  return (
    <div className="relative rounded-npc border border-npc-border-soft px-npc-gap-sm py-npc-gap-xs">
      <Link
        to="/hosts/$hostId"
        params={{ hostId: host.id }}
        aria-label={host.name}
        className="absolute inset-0"
      />
      <div className="relative flex flex-wrap items-center gap-npc-gap-xs">
        <div className="min-w-0 text-npc-body font-medium">{host.name}</div>
        <StatusChip host={host} />
      </div>
      {showIp ? (
        <div className="mt-[2px] font-npc-mono text-npc-meta-sm text-npc-ink-softer">{primary}</div>
      ) : null}
      <div className="mt-[2px] text-npc-caption text-npc-ink-soft">{capacityText(host)}</div>
      <p className="mb-0 mt-[2px] line-clamp-1 text-npc-caption leading-[1.5] text-npc-ink-medium">
        {parentName(host.parent) ? (
          <T id="host.carve.lives_on" vars={{ parent_name: parentName(host.parent)! }} />
        ) : (
          hostSummaryText(host.summary)
        )}
      </p>
    </div>
  )
}

function HostMetaStrip({ host }: { host: Schemas['HostSummary'] }) {
  const agent = host.agent_last_seen_at ? ago(host.agent_last_seen_at) : t('host.card.unknown')
  return (
    <div className="mt-[2px] text-npc-caption leading-[1.5] text-npc-ink-soft">
      <span>{capacityText(host)}</span>
      <span className="text-npc-ink-softer"> · </span>
      <span>
        <T id="host.card.agent" /> {agent}
      </span>
      <span className="text-npc-ink-softer"> · </span>
      <span>{activityText(host)}</span>
    </div>
  )
}

function capacityText(host: Schemas['HostSummary']): string {
  if (host.probe_live && host.capacity_text) return host.capacity_text
  return t('host.card.unknown')
}

function activityText(host: Schemas['HostSummary']): ReactNode {
  const action = host.last_action
  if (!action?.text) return <T id="host.card.uncollected" />
  return (
    <>
      {action.text}
      {action.occurred_at ? <span className="text-npc-ink-softer"> · {ago(action.occurred_at)}</span> : null}
    </>
  )
}

function HostWorkloads({ host }: { host: Schemas['HostSummary'] }) {
  if (!host.probe_live) {
    return <div className="mt-npc-gap-xs text-npc-caption text-npc-ink-softer">{t('host.card.unknown')}</div>
  }
  const labels = uniqueShortWorkloads((host.workloads ?? []).map((item) => item.name))
  if (labels.length === 0) {
    return <div className="mt-npc-gap-xs text-npc-caption text-npc-ink-softer">{t('host.card.uncollected')}</div>
  }
  const shown = labels.slice(0, 4)
  const extra = labels.length - shown.length
  return (
    <div className="mt-npc-gap-xs flex flex-wrap items-center gap-npc-gap-xs">
      {shown.map((label) => (
        <Tag key={label} tone="neutral">
          {label}
        </Tag>
      ))}
      {extra > 0 ? (
        <span className="text-npc-caption text-npc-ink-softer">
          <T id="host.card.more_workloads" vars={{ count: extra }} />
        </span>
      ) : null}
    </div>
  )
}

function HostAddresses({ host }: { host: Schemas['HostSummary'] }) {
  const addresses = hostAddresses(host)
  const { primary, extra } = pickPrimaryAddress(addresses)
  if (!primary) return null
  return (
    <div className="mt-[2px] font-npc-mono text-npc-meta-sm text-npc-ink-softer">
      <span>{primary}</span>
      {extra > 0 ? (
        <span className="ml-npc-gap-xs">
          <T id="host.card.more_addresses" vars={{ count: extra }} />
        </span>
      ) : null}
    </div>
  )
}

function StatusChip({ host }: { host: Schemas['HostSummary'] }) {
  if (host.reach?.kind === 'credential') {
    return (
      <Tag tone="warn">
        <T id="host.status.credential" />
      </Tag>
    )
  }
  if (!host.status_text) return null
  return <Tag tone={statusTone(host)}>{host.status_text}</Tag>
}

function hostSummaryText(summary: string | null | undefined): string {
  const text = summary?.trim() ?? ''
  return text || t('host.summary.unassigned')
}

function hostAddresses(host: Schemas['HostSummary']): string[] {
  const raw = host.addresses?.length ? host.addresses : [host.address]
  return raw.map((ip) => ip.trim()).filter(Boolean)
}

export function pickPrimaryAddress(addresses: string[]): { primary?: string; extra: number } {
  if (addresses.length === 0) return { extra: 0 }
  const notLinkLocalV6 = addresses.filter((ip) => !isLinkLocalV6(ip))
  const ipv4 = notLinkLocalV6.filter(isIPv4)
  const preferred = ipv4.filter((ip) => !isApipa(ip))
  const primary = preferred[0] ?? ipv4[0] ?? notLinkLocalV6[0] ?? addresses[0]
  const extra = addresses.filter((ip) => ip !== primary).length
  return { primary, extra }
}

function isIPv4(ip: string): boolean {
  return /^(?:\d{1,3}\.){3}\d{1,3}$/.test(ip)
}

function isLinkLocalV6(ip: string): boolean {
  return ip.toLowerCase().startsWith('fe80:')
}

function isApipa(ip: string): boolean {
  return ip.startsWith('169.254.')
}

export function shortWorkloadLabel(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean)
  const first = tokens.find((token) => !token.startsWith('-')) ?? tokens[0] ?? ''
  if (!first) return ''
  if (first.includes('/')) {
    const parts = first.split('/').filter(Boolean)
    return parts.at(-1) ?? first
  }
  return first
}

export function uniqueShortWorkloads(names: string[]): string[] {
  const seen = new Set<string>()
  const labels: string[] = []
  for (const name of names) {
    const label = shortWorkloadLabel(name)
    if (!label || seen.has(label)) continue
    seen.add(label)
    labels.push(label)
  }
  return labels
}


function needsNod(host: Schemas['HostSummary']): boolean {
  return host.occupancy === 'unassigned' || Boolean(host.suggestion?.parent_host_id)
}

function isException(host: Schemas['HostSummary']): boolean {
  if (host.status === 'offline' || host.status === 'error' || host.status === 'reclaimed') return true
  if (
    host.reach?.kind === 'credential' ||
    host.reach?.kind === 'blocked' ||
    host.reach?.kind === 'host_key_changed'
  ) {
    return true
  }
  return host.status_tone === 'danger' || host.status_tone === 'warn'
}

function bucketOf(host: Schemas['HostSummary'], decisionIds: Set<string>): Bucket {
  if (decisionIds.has(host.id) || needsNod(host)) return 'decision'
  if (isException(host)) return 'exception'
  if (host.occupancy === 'shared') return 'available'
  return 'assigned'
}

function splitPool(
  hosts: Schemas['HostSummary'][],
  decisionIds: Set<string>,
): { available: Row[]; assigned: Row[]; exception: Row[] } {
  const pool = hosts.filter((host) => bucketOf(host, decisionIds) !== 'decision')
  const present = new Set(pool.map((host) => host.id))
  const parentBucket = new Map<string, Exclude<Bucket, 'decision'>>()
  for (const host of pool) {
    const bucket = bucketOf(host, decisionIds)
    if (bucket !== 'decision') parentBucket.set(host.id, bucket)
  }

  const family = new Map<string, Schemas['HostSummary'][]>()
  const tops: Schemas['HostSummary'][] = []
  for (const host of pool) {
    const parent = host.parent
    const parentInPool = parent && present.has(parent.id)
    const parentGroup = parent ? parentBucket.get(parent.id) : undefined
    const selfGroup = bucketOf(host, decisionIds)
    if (
      parentInPool &&
      parentGroup &&
      parentGroup !== 'exception' &&
      selfGroup !== 'exception'
    ) {
      const siblings = family.get(parent.id) ?? []
      siblings.push(host)
      family.set(parent.id, siblings)
      continue
    }
    tops.push(host)
  }

  const available: Row[] = []
  const assigned: Row[] = []
  const exception: Row[] = []
  for (const host of tops) {
    const row = { host, children: family.get(host.id) ?? [] }
    switch (bucketOf(host, decisionIds)) {
      case 'available':
        available.push(row)
        break
      case 'assigned':
        assigned.push(row)
        break
      case 'exception':
        exception.push(row)
        break
    }
  }
  return { available, assigned, exception }
}

function countHosts(rows: Row[]): number {
  return rows.reduce((sum, row) => sum + 1 + row.children.length, 0)
}

